import React, { useEffect, useRef, useState } from "react";
import CallChat, { stripHarakat } from "../CallChat";
import { fetchOnboardingAudioManifest, onboardingAudioUrl } from "../../api";
import { track } from "../../utils/analytics";
import { CALL_SIM } from "./callSimContent";

/**
 * OnboardingCallSim — scripted practice-call taste for speaking-motivated
 * funnel visitors. Renders the REAL call interface — PracticeCallPage's
 * full-screen layout (harakat toggle, countdown, video-tile strip, the
 * "Call chat" panel via the actual CallChat component, the suggestions
 * panel, the transcript-draft panel) — but every AI action is canned:
 * Translate reveals a bundled translation, Suggest a reply serves the
 * scripted suggestion, Send posts it and triggers Yusuf's follow-up.
 * Zero cost, zero permissions, works in social in-app webviews; the only
 * live piece is Yusuf's voice via the whitelisted TTS clips.
 */
const CALL_SECONDS = 15 * 60;

export default function OnboardingCallSim({ onContinue }) {
  const [ex1, ex2] = CALL_SIM.exchanges;
  const [messages, setMessages] = useState(() => [
    { role: "partner", name: CALL_SIM.partner, arabic: ex1.arabic, ts: 0 },
  ]);
  const [showHarakat, setShowHarakat] = useState(true);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [draft, setDraft] = useState(null); // { arabic, translation }
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [done, setDone] = useState(false); // Yusuf's follow-up arrived
  const [secsLeft, setSecsLeft] = useState(CALL_SECONDS - 23);
  const [audioKeys, setAudioKeys] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    track("onboarding-call-view", {});
    fetchOnboardingAudioManifest()
      .then((m) => {
        if (m?.enabled && Array.isArray(m.keys)) setAudioKeys(new Set(m.keys));
      })
      .catch(() => {});
    const tick = setInterval(() => setSecsLeft((s) => Math.max(0, s - 1)), 1000);
    const timers = timersRef.current;
    return () => {
      clearInterval(tick);
      audioRef.current?.pause();
      timers.forEach(clearTimeout);
    };
  }, []);

  const later = (ms, fn) => timersRef.current.push(setTimeout(fn, ms));

  const play = (key) => {
    try {
      audioRef.current?.pause();
      const audio = new Audio(onboardingAudioUrl(key));
      audioRef.current = audio;
      setPlayingKey(key);
      audio.addEventListener("ended", () => setPlayingKey(null));
      audio.play().catch(() => setPlayingKey(null));
      track("onboarding-call-play", { key });
    } catch {
      setPlayingKey(null);
    }
  };

  // CallChat's "Translate" action — canned, with the real busy beat
  // (CallChat itself shows "Translating…" while this promise is pending).
  const correctMessage = (i, m) => {
    track("onboarding-call-translate", { msg: i });
    return new Promise((resolve) => {
      later(800, () => {
        const ex = CALL_SIM.exchanges.find((e) => e.arabic === m.arabic);
        setMessages((prev) =>
          prev.map((msg, idx) => (idx === i ? { ...msg, translation: ex?.translation || msg.translation } : msg))
        );
        resolve();
      });
    });
  };

  // CallChat's "💡 Suggest reply" action — canned suggestion after a beat.
  const fireSuggest = () => {
    if (suggestBusy || draft) return;
    track("onboarding-call-suggest", {});
    setSuggestBusy(true);
    later(900, () => {
      setSuggestBusy(false);
      setSuggestions([ex1.reply]);
    });
  };

  const pickSuggestion = (r) => {
    setSuggestions([]);
    setDraft(r);
  };

  const send = () => {
    if (!draft) return;
    track("onboarding-call-send", {});
    setMessages((prev) => [...prev, { role: "me", name: "You", arabic: draft.arabic, translation: draft.translation }]);
    setDraft(null);
    setPartnerTyping(true);
    later(1500, () => {
      setPartnerTyping(false);
      setDone(true);
      setMessages((prev) => [...prev, { role: "partner", name: CALL_SIM.partner, arabic: ex2.arabic }]);
      // Best-effort voice note — mobile browsers may block play() this far
      // from the tap; the tile play button still works.
      if (audioKeys?.has(ex2.audioKey)) play(ex2.audioKey);
    });
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const yusufSpeaking = playingKey === ex1.audioKey || playingKey === ex2.audioKey;
  const currentClip = done ? ex2.audioKey : ex1.audioKey;

  // Coach cues, one target at a time through the first loop: ✦ → Translate →
  // ✦ → Suggest reply → the suggestion → Send. After Yusuf's follow-up the
  // visitor knows the mechanic — no more pulses.
  const replySent = messages.some((m) => m.role === "me");
  const chatCoach = !messages[0]?.translation
    ? "coach-msg-actions coach-translate"
    : !replySent && !draft && !suggestBusy && suggestions.length === 0
      ? "coach-msg-actions coach-suggest"
      : "";

  return (
    <div className="w-full">
      <h2 className="text-xl sm:text-2xl font-semibold text-dt-parchment mb-1.5">
        Too shy to speak? This is for you
      </h2>
      <p className="text-sm text-dt-parchment-dim mb-4">{CALL_SIM.intro}</p>

      {/* ── The real call interface, scripted ─────────────────────────── */}
      <div className="flex flex-col gap-3">
        {/* Header: harakat toggle · title + countdown (mirrors PracticeCallPage) */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => setShowHarakat((v) => !v)}
            role="switch"
            aria-checked={showHarakat}
            title="Show or hide harakat in the transcript"
            className="surface-glass flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full"
          >
            <span className="relative inline-flex h-5 w-9 rounded-full transition-colors"
              style={{ background: showHarakat ? "var(--gold)" : "var(--forest)" }}>
              <span className="inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5"
                style={{ transform: showHarakat ? "translateX(18px)" : "translateX(2px)" }} />
            </span>
            <span className="text-sm font-medium" style={{ color: "var(--parchment)" }}>Harakat</span>
          </button>
          <div className="text-right">
            <p className="text-base font-bold leading-tight" style={{ color: "var(--parchment)" }}>
              Practice conversation
            </p>
            <p className="text-xs" style={{ color: "var(--gold-dim)" }}>⏱ {fmt(secsLeft)}</p>
          </div>
        </div>

        {/* Video tiles — the real mobile strip (VideoTile's no-camera look) */}
        <div className="shrink-0 h-24 sm:h-28 flex gap-2">
          <SimTile
            initial="ي"
            label={CALL_SIM.partnerEn}
            active={yusufSpeaking}
            typing={partnerTyping}
            onPlay={audioKeys?.has(currentClip) ? () => play(currentClip) : null}
            playing={yusufSpeaking}
          />
          <SimTile initial="A" label="You" micOff />
        </div>

        {/* Call chat — the real CallChat component with canned callbacks */}
        <div className="flex flex-col surface-glass rounded-2xl px-3 pt-2.5 pb-3">
          <div className="flex items-center justify-between pb-2"
            style={{ borderBottom: "1px solid var(--panel-line)" }}>
            <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "var(--gold-dim)" }}>
              💬 Call chat
            </span>
            <span className="text-[10px]" style={{ color: "var(--parchment-dim)" }}>
              visible to everyone on the call
            </span>
          </div>
          <div className={`flex flex-col pt-2 ${chatCoach}`}>
            <CallChat
              messages={messages}
              myName="You"
              partnerName={CALL_SIM.partnerEn}
              showHarakat={showHarakat}
              onCorrectMessage={correctMessage}
              onSuggestReply={fireSuggest}
              tappableWords={false}
              compact
            />
            {partnerTyping && (
              <p className="text-xs mt-1 px-1" style={{ color: "var(--parchment-dim)" }}>
                {CALL_SIM.partnerEn} is typing…
              </p>
            )}
          </div>
        </div>

        {/* Reply suggestions — real panel, canned content */}
        {(suggestBusy || suggestions.length > 0) && (
          <div className="surface-glass rounded-2xl px-3 py-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "var(--gold-dim)" }}>
                Suggested replies — tap to load into your draft
              </span>
              {suggestions.length > 0 && (
                <button onClick={() => setSuggestions([])} aria-label="Dismiss suggestions"
                  className="text-xs" style={{ color: "var(--parchment-dim)" }}>✕</button>
              )}
            </div>
            {suggestBusy ? (
              <div className="flex items-center gap-2 text-sm py-1" style={{ color: "var(--parchment-dim)" }}>
                <Spinner /> Thinking of a reply…
              </div>
            ) : (
              <div className="space-y-1.5">
                {suggestions.map((r, i) => (
                  <button key={i} onClick={() => pickSuggestion(r)}
                    className="w-full text-left rounded-xl px-3 py-2 dt-ghost-btn coach-pulse">
                    <p dir="rtl" lang="ar" className="font-arabic text-lg leading-loose" style={{ color: "var(--parchment)" }}>
                      {showHarakat ? r.arabic : stripHarakat(r.arabic)}
                    </p>
                    {r.translation && <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>{r.translation}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transcript draft — real dashed panel; here it's the loaded suggestion */}
        {draft && (
          <div className="rounded-2xl px-3.5 py-3"
            style={{ border: "1px dashed var(--gold-dim)", background: "rgba(18, 48, 35, 0.35)" }}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>↺ Redo</span>
              <span className="text-xs font-semibold" style={{ color: "var(--tag-teal)" }}>
                Transcript draft · ready to send
              </span>
            </div>
            <p dir="rtl" lang="ar" className="font-arabic text-lg leading-loose" style={{ color: "var(--parchment)" }}>
              {showHarakat ? draft.arabic : stripHarakat(draft.arabic)}
            </p>
            <p className="text-xs mb-2" style={{ color: "var(--parchment-dim)" }}>{draft.translation}</p>
            <button
              onClick={send}
              className="w-full px-3 py-2 rounded-xl bg-dt-gold text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter] coach-pulse"
            >
              Send to call chat ↩
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-dt-parchment-dim mt-3">
        {done ? CALL_SIM.outro : `${CALL_SIM.genderNote} Tap the ✦ beside Yusuf's message.`}
      </p>

      {/* Never gate the funnel on playing along. */}
      <button
        type="button"
        onClick={onContinue}
        className="w-full mt-5 px-4 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
      >
        {done ? "I could actually do this" : "Continue"}
      </button>
    </div>
  );
}

// Mirrors PracticeCallPage's VideoTile in its no-camera state (initial in a
// rounded square, name chip, gold ring while "speaking"), plus an optional
// voice-note play control — on a real call you hear your partner through
// the call itself; here the whitelisted TTS clip stands in.
function SimTile({ initial, label, active = false, micOff = false, typing = false, onPlay = null, playing = false }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl flex-1 min-w-0"
      style={{
        background: "var(--forest)",
        border: `1px solid ${active ? "var(--gold)" : "var(--panel-line)"}`,
        boxShadow: active ? "0 0 0 1px rgba(201, 162, 39, 0.35)" : "none",
      }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg font-bold select-none font-amiri"
          style={{ background: "var(--forest-softer)", color: "var(--parchment)", border: "1px solid var(--panel-line)" }}
        >
          {initial}
        </span>
      </div>
      <span
        className="absolute bottom-1.5 right-2 max-w-[85%] truncate text-[11px] font-medium px-2 py-0.5 rounded-full"
        style={{ background: "rgba(6, 16, 12, 0.55)", color: "var(--parchment)" }}
      >
        {typing ? `${label} · typing…` : label}
      </span>
      {micOff && (
        <span
          className="absolute bottom-1.5 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px]"
          style={{ background: "rgba(6, 16, 12, 0.55)", color: "var(--tag-rose)" }}
          title="Microphone muted"
        >
          ✕
        </span>
      )}
      {onPlay && (
        <button
          type="button"
          onClick={onPlay}
          aria-label={`Hear ${label}`}
          className="absolute top-1.5 left-2 px-2 py-0.5 rounded-full text-[11px] transition-colors"
          style={{ background: "rgba(6, 16, 12, 0.55)", color: playing ? "var(--gold-bright)" : "var(--parchment)" }}
        >
          {playing ? "▮▮" : "🔊"}
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block w-3.5 h-3.5 rounded-full animate-spin"
      style={{ border: "2px solid var(--gold-dim)", borderTopColor: "var(--gold)" }}
    />
  );
}
